/**
 * CSRF-aware fetch wrapper (TODO #46, 2026-06-05)
 *
 * Drop-in replacement for `fetch()` that:
 *   1. On the first state-changing call (POST/PUT/PATCH/DELETE), ensures a
 *      CSRF token is in hand by hitting GET /api/auth/csrf. Subsequent calls
 *      reuse the cached token.
 *   2. Attaches `x-csrf-token` header to state-changing requests.
 *   3. Defaults `credentials: 'include'` so the session cookie + the
 *      __Host-decisivos.csrf cookie flow.
 *   4. Coordinates concurrent first-time mints via a single in-flight
 *      promise (no thundering-herd of /csrf fetches on app boot).
 *
 * GET / HEAD / OPTIONS requests skip the token attach (the server's
 * doubleCsrfProtection ignores those methods anyway), so they're effectively
 * a passthrough to native fetch — no per-call overhead.
 *
 * Token rotation: not implemented. The server's csrf-csrf v4 setup uses a
 * stable HMAC; the token only changes when the express-session ID changes
 * (login, logout, regenerate). UserContext calls clearCsrfToken() at those
 * boundaries so the next state-changing call refreshes.
 */

const CSRF_ENDPOINT = '/api/auth/csrf';
const CSRF_HEADER = 'x-csrf-token';
const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Mirror the backend's NODE_ENV=test CSRF bypass (server/index.js): in
// vitest runs we skip the mint round-trip and use a placeholder token.
// The backend doesn't validate it anyway when CSRF is off. Without this,
// every existing global.fetch=vi.fn(...) mock would have to add a
// /api/auth/csrf handler.
const IS_TEST = typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test';

let cachedToken = IS_TEST ? 'test-csrf-token-bypass' : null;
let inFlightFetch = null;

async function fetchToken() {
  if (cachedToken) return cachedToken;
  if (inFlightFetch) return inFlightFetch;

  inFlightFetch = (async () => {
    try {
      const res = await fetch(CSRF_ENDPOINT, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`CSRF mint failed: ${res.status}`);
      }
      const data = await res.json();
      if (!data?.csrfToken) {
        throw new Error('CSRF mint returned no csrfToken');
      }
      cachedToken = data.csrfToken;
      return cachedToken;
    } finally {
      inFlightFetch = null;
    }
  })();

  return inFlightFetch;
}

/**
 * Clear the cached CSRF token. Call this when the session identity changes
 * (login, logout, regenerate) so the next state-changing request mints a
 * fresh token bound to the new req.session.id.
 */
export function clearCsrfToken() {
  cachedToken = null;
  inFlightFetch = null;
}

/**
 * For tests: hook to set a known token so a test can assert the header
 * is attached without round-tripping through the mint endpoint.
 */
export function _setCsrfTokenForTests(token) {
  cachedToken = token;
}

/**
 * CSRF-aware fetch. Identical signature to native fetch(); attaches
 * `x-csrf-token` header to state-changing methods.
 *
 * Self-heals stale tokens: if a state-changing request comes back 403 with
 * a body that mentions "invalid csrf token", the cached token is wiped, a
 * fresh one is minted, and the original request is retried exactly once.
 * Surfaced 2026-06-09 — symptom: a KYC user left the verify-email page open
 * across an admin approval round-trip, then clicked Back to sign in and hit
 * "invalid csrf token" on login POST. The cached token's HMAC was bound to
 * a session id that had since rotated (verify-email/confirm trusted-domain
 * branch regenerates the session at server/routes/auth.js:1132).
 *
 * @param {string|URL|Request} url
 * @param {RequestInit} [opts]
 */
export async function csrfFetch(url, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  const needsToken = STATE_CHANGING.has(method);

  const doFetch = async () => {
    const requestInit = {
      credentials: 'include',
      ...opts,
    };
    if (needsToken) {
      const token = await fetchToken();
      requestInit.headers = {
        ...(opts.headers || {}),
        [CSRF_HEADER]: token,
      };
    }
    return fetch(url, requestInit);
  };

  const response = await doFetch();

  // Self-healing retry path. Only checked for state-changing methods (GET/HEAD
  // never carry the header and the server ignores them anyway). Cloning the
  // response so the caller still sees an unread body on the returned object
  // if no retry happens.
  if (needsToken && response.status === 403) {
    let bodyText = '';
    try { bodyText = await response.clone().text(); } catch { /* unreadable — skip retry */ }
    if (bodyText.toLowerCase().includes('invalid csrf token')) {
      clearCsrfToken();
      return doFetch();
    }
  }

  return response;
}

export default csrfFetch;
